/**
 * Patient-document constants + helpers for the header's "All documents" popup
 * (Figma 2646:3879). Ported from bridge:
 *   - DocumentType   → constants/type/document/DocumentType.js
 *   - FileFormat     → constants/type/file/FileFormat.js
 *   - category labels → the documents module's `folderRevised` map
 *
 * The list endpoint (getByPatientDoc) already groups by type server-side, so a
 * response element is { type, count, all: [document] }.
 */

/** Server DocumentType values — non-sequential; do not renumber. */
export const DOCUMENT_TYPE = Object.freeze({
  DETAILED_ASSESSMENT: 1,
  HEALTH_PLAN: 2,
  EXERCISE_PLAN: 3,
  FOOD_LIST: 5,
  AD_HOC: 6,
  RECIPES: 7,
  MEDICAL_RECORDS: 8,
  PRESCRIPTION: 9,
  FOOT_ULCER: 10,
  LAB_REPORT: 11,
  CLAIMS: 12,
});

/** code → display label (bridge's folderRevised). Header renders it uppercased. */
const DOC_CATEGORY_LABEL = {
  11: "Lab reports",
  9: "Prescriptions",
  8: "Medical records",
  2: "Health plans",
  3: "Exercise plans",
  1: "Detailed assessments",
  5: "Food lists",
  7: "Recipes",
  10: "Foot ulcer",
  12: "Claims",
  6: "Other media",
};

/** Figma order: Lab reports, Prescriptions, Medical records, …, Other media last. */
const DOC_CATEGORY_ORDER = [11, 9, 8, 2, 3, 1, 5, 7, 10, 12, 6];

/**
 * The four folders the design always draws (Figma 3023:9382), even when a
 * patient has none of that kind — "Medical records / No documents" is drawn
 * greyed out on the board, which is only meaningful if the tile is fixed.
 *
 * The server does NOT return an empty folder (it builds its groups FROM the
 * documents), so these four are synthesized client-side at count 0. Every
 * OTHER category still gets a tile when it has documents — a health plan PDF
 * must not become unreachable because the board only had room to draw four.
 */
export const PINNED_FOLDERS = [11, 9, 8, 6];

/**
 * Legacy prescriptions were type 4 before the server started writing 9, and it
 * still returns both as separate folders. They are one folder to a clinician,
 * and phr has no label for 4 — untranslated it renders as "Documents", sorted
 * last, which reads like a bug. Folded at the edge so nothing downstream has to
 * know: the tile, the count and the drill-down all see one Prescriptions.
 */
const LEGACY_TYPE = { 4: 9 };
export const canonicalType = (type) => LEGACY_TYPE[type] || type;

/**
 * The "Type of document" select's options — every folder a document can be
 * filed into, in the same order the popup draws its tiles.
 *
 * Built from DOC_CATEGORY_ORDER rather than from the patient's own folders: the
 * tiles only cover types this patient already has (plus the four pinned ones),
 * so driving the select off them would offer a different list per patient.
 *
 * Legacy type 4 is absent by construction — it is in neither the order array
 * nor the label map — so nothing here can mint one. canonicalType stays a READ
 * -path fold and is deliberately NOT applied to this value. (The server folds
 * 4 → 9 on save anyway, so the client rule is a UX guarantee, not the boundary.)
 */
export const DOC_TYPE_OPTIONS = DOC_CATEGORY_ORDER.map((value) => ({
  value,
  label: DOC_CATEGORY_LABEL[value],
}));

/**
 * What the upload accepts. The extension list is the server's own
 * ALLOWED_FILE_EXTENSIONS (document-service .env), so a file that passes here
 * is one the server will also take; anything else is rejected with a 500 and an
 * opaque "Not Allowed Extension".
 *
 * The cap was the DESIGN's 10 MB (Figma 6326:6687 "PDF, JPG or PNG · up to
 * 10 MB") and is now 20 MB (Nittin, 2026-09-12). The service enforces no size
 * limit of its own, so this is the only thing standing between a doctor and a
 * 400 MB upload that fails somewhere in the gateway with no useful message.
 *
 * ONE SOURCE FOR THE NUMBER. The limit used to be written out again in the
 * drop-zone caption and a third time in the rejection message, so raising it
 * meant finding three places and the UI could disagree with the check it
 * describes. UPLOAD_MAX_LABEL is derived from the bytes.
 */
export const UPLOAD_ACCEPT_EXT = ["pdf", "jpg", "jpeg", "png"];
export const UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
export const UPLOAD_MAX_LABEL = `${Math.round(UPLOAD_MAX_BYTES / (1024 * 1024))} MB`;

/**
 * How long a document's optional description may be (Nittin, 2026-09-12).
 *
 * Same rule as the byte cap above: ONE source for the number, so the counter
 * the operator reads cannot disagree with the limit the field enforces.
 */
export const DESCRIPTION_MAX_CHARS = 250;

/**
 * The server derives a file's extension by slicing a []rune with BYTE offsets
 * (document-service utils/file.go GetExtension), so any multibyte character in
 * the name panics the handler into a 500 — "reporté.pdf" is enough. Nothing
 * client-side can fix that, but we can avoid sending one: the upload keeps the
 * user's title intact and sends an ASCII-safe fileName.
 */
export const asciiFileName = (name) => {
  const safe = String(name || "").replace(/[^\x20-\x7E]/g, "_").trim();
  return safe || "document";
};

export const categoryLabel = (type) => DOC_CATEGORY_LABEL[type] || "Documents";
export const categoryOrderIndex = (type) => {
  const i = DOC_CATEGORY_ORDER.indexOf(type);
  return i === -1 ? DOC_CATEGORY_ORDER.length : i;
};

/** Server FileFormat values → extension label shown on the type badge. */
const FILE_FORMAT = { 1: "JPG", 2: "PNG", 3: "GIF", 4: "PDF", 5: "MP3", 6: "MP4" };

/**
 * The reverse map, for UPLOADS: extension → the server's FileFormat int.
 *
 * Derived from the FILE NAME, not the MIME type. Browsers disagree about the
 * MIME for a .jpg (image/jpeg vs image/jpg vs, on some Windows setups,
 * application/octet-stream), and the server itself falls back to the extension
 * when it sees octet-stream (document-service controllers/document.go). The
 * extension is the one thing both ends agree on.
 */
const FORMAT_BY_EXT = { jpg: 1, jpeg: 1, png: 2, gif: 3, pdf: 4, mp3: 5, mp4: 6 };
export const fileExtension = (name) =>
  String(name || "").split(".").pop().toLowerCase();
export const formatFromFileName = (name) => FORMAT_BY_EXT[fileExtension(name)] || 0;
export const formatLabel = (format) => FILE_FORMAT[format] || "FILE";
/** Badge tone class: images green, pdf navy, audio/video amber, else grey. */
export const formatTone = (format) => {
  if (format === 1 || format === 2 || format === 3) return "img";
  if (format === 4) return "pdf";
  if (format === 5 || format === 6) return "av";
  return "other";
};

const MON = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
/**
 * updatedAt → "13 Feb 2026". Handles ISO and bridge's "DD-MM-YYYY HH:mm".
 *
 * The DD-MM-YYYY branch MUST come first: `new Date("05-03-2026")` doesn't throw —
 * V8 reads a hyphenated date as MM-DD-YYYY, so 5 Mar would silently render as
 * 3 May. Only day>12 makes new Date() fail, so a plain "isNaN then fall back"
 * mislabels ~12/31 of documents. Match the bridge format explicitly instead.
 */
export const formatDocDate = (raw) => {
  if (!raw) return "";
  let d;
  const m = String(raw).match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (m) {
    d = new Date(Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])));
  } else {
    d = new Date(raw); // ISO instant
  }
  if (isNaN(d.getTime())) return "";
  return `${d.getUTCDate()} ${MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/**
 * Where PatientService.generateDocToken stashes the {token, expiredTime}.
 *
 * The SHARED key — the one the network client re-mints into on every 401
 * refresh (newAPIService.saveNewDocToken) and DigitisationService reads and
 * writes. Under a key of its own ("zy.DOC_TOKEN", as it was) this menu's token
 * went stale the moment the access token refreshed, while every other document
 * opener had the fresh one. Same name in phr and bridge (different origins, so
 * no collision).
 */
export const DOC_TOKEN_STORAGE_KEY = "zy.bridge.doc.token";

const readDocToken = () => {
  try {
    const raw = localStorage.getItem(DOC_TOKEN_STORAGE_KEY);
    if (!raw) return "";
    const obj = JSON.parse(raw);
    return (obj && obj.token) || "";
  } catch (e) {
    return "";
  }
};

/**
 * The openable file URL — bridge's setUrl: force https, then append the doc
 * token as a query param (documents are token-gated, not per-request-signed).
 * Degrades to the raw https URL when no token has been generated.
 */
export const buildDocUrl = (url) => {
  if (!url) return "";
  let u = url;
  if (u.slice(0, 5) === "http:") u = `https${u.slice(4)}`;
  const token = readDocToken();
  if (!token) return u;
  return `${u}${u.indexOf("?") === -1 ? "?" : "&"}token=${encodeURIComponent(token)}`;
};

/** Open a document in a new tab (bridge's "open in new tab"). */
export const openDocument = (doc) => {
  const u = buildDocUrl(doc && doc.url);
  if (u) window.open(u, "_blank", "noopener,noreferrer");
};

/**
 * Coerce the list response into `[{ type, all: [doc] }]`, grouping a flat list.
 *
 * Also MERGES folders that share a canonical type — the server returns legacy
 * prescriptions (4) and current ones (9) as two folders, and a doctor should
 * see one. Merging here rather than at render keeps the count, the tile and the
 * drill-down from disagreeing.
 */
export const normalizeDocGroups = (res) => {
  const raw = Array.isArray(res)
    ? res
    : res && Array.isArray(res.documents)
    ? Object.entries(
        res.documents.reduce((acc, d) => {
          const t = d && d.type;
          (acc[t] = acc[t] || []).push(d);
          return acc;
        }, {})
      ).map(([t, all]) => ({ type: Number(t), all }))
    : [];

  const merged = [];
  const byType = {};
  raw.forEach((g) => {
    if (!g) return;
    const t = canonicalType(g.type);
    if (!byType[t]) {
      byType[t] = { ...g, type: t, all: [...(g.all || [])] };
      merged.push(byType[t]);
      return;
    }
    byType[t].all = byType[t].all.concat(g.all || []);
  });
  return merged;
};
